import React, { useEffect, useState } from 'react';
/**
 * `NavStepper` renders the journey breadcrumb/stepper (desktop + mobile).
 *
 * Behaviour:
 * - Determines the active step from the current route (`currentPath`).
 * - Marks steps complete based on `navigationStatus` and product selection.
 * - Renders a Material UI `Stepper` for desktop and a condensed summary for
 *   mobile.
 *
 * `GetNavDispatch` maps a route to a JourneyReducer action for updating
 * `navigationStatus`.
 */
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StepLabel from '@mui/material/StepLabel';
import { PageAbsolutePath } from '../../consts/Common';
import { ProductType, StepperStatus } from './CommonTypes';
import { Action } from '../../journeyState/JourneyReducer';
import { useTranslation } from 'react-i18next';

type NavStepperProps = {
  products: ProductType[];
  navigationStatus: StepperStatus;
  currentPath: string;
};

export const GetNavDispatch = (
  page: string,
  status: boolean
): Action | undefined => {
  switch (page) {
    case PageAbsolutePath.VehicleDetails:
    case PageAbsolutePath.Renewals.VehicleDetails:
    case PageAbsolutePath.Customer.VehicleDetails: {
      return {
        type: 'set-nav-status',
        key: 'vehicle',
        value: status
      };
    }
    case PageAbsolutePath.Products:
    case PageAbsolutePath.CreatePlan:
    case PageAbsolutePath.Customer.Products:
    case PageAbsolutePath.Customer.CreatePlan: {
      return {
        type: 'set-nav-status',
        key: 'products',
        value: status
      };
    }
    case PageAbsolutePath.Renewals.Quote: {
      return {
        type: 'set-nav-status',
        key: 'quote',
        value: status
      };
    }
    case PageAbsolutePath.PersonalDetails:
    case PageAbsolutePath.Renewals.PersonalDetails:
    case PageAbsolutePath.Customer.PersonalDetails: {
      return {
        type: 'set-nav-status',
        key: 'personalDetails',
        value: status
      };
    }
    case PageAbsolutePath.Summary:
    case PageAbsolutePath.DirectDebit:
    case PageAbsolutePath.SinglePayment:
    case PageAbsolutePath.Renewals.Summary:
    case PageAbsolutePath.Customer.Summary:
    case PageAbsolutePath.Customer.DirectDebit:
    case PageAbsolutePath.Customer.SinglePayment: {
      return {
        type: 'set-nav-status',
        key: 'checkout',
        value: status
      };
    }
    case PageAbsolutePath.Confirmation:
    case PageAbsolutePath.Renewals.Confirmation:
    case PageAbsolutePath.Customer.Confirmation: {
      return {
        type: 'set-nav-status',
        key: 'confirmation',
        value: status
      };
    }
    default: {
      return undefined;
    }
  }
};
const NavStepper = React.memo(
  function NavStepper({
    products,
    navigationStatus,
    currentPath
  }: NavStepperProps): JSX.Element {
    const { t } = useTranslation();
    const stepsArray = [
      { stepName: t('breadcrumb.vehicle-details'), completed: false },
      { stepName: t('breadcrumb.products'), completed: false },
      { stepName: t('breadcrumb.personal-details'), completed: false },
      { stepName: t('breadcrumb.checkout'), completed: false },
      { stepName: t('breadcrumb.confirmation'), completed: false }
    ];

    const [steps, setSteps] = useState(stepsArray);
    const numberOfSteps = steps.length;

    const isStepCompleted = (stepId: number): boolean => {
      switch (stepId) {
        case 0: {
          return navigationStatus.vehicle;
        }
        case 1: {
          if (
            products === null ||
            products === undefined ||
            products.length === 0 ||
            products.filter((p) => p.isSelected === true).length === 0
          )
            return false;
          else return navigationStatus.products;
        }
        case 2: {
          return navigationStatus.personalDetails;
        }
        case 3: {
          return navigationStatus.checkout;
        }
        default: {
          return false;
        }
      }
    };

    const getActiveStepIndex = (): number => {
      switch (currentPath) {
        case PageAbsolutePath.VehicleDetails: {
          return 0;
        }
        case PageAbsolutePath.Products:
        case PageAbsolutePath.CreatePlan: {
          return 1;
        }
        case PageAbsolutePath.PersonalDetails: {
          return 2;
        }
        case PageAbsolutePath.Summary:
        case PageAbsolutePath.DirectDebit:
        case PageAbsolutePath.SinglePayment: {
          return 3;
        }
        case PageAbsolutePath.Confirmation: {
          return 4;
        }
        case PageAbsolutePath.FailedRegistration: {
          return 5;
        }
        default: {
          return 0;
        }
      }
    };

    const getLabelContent = (index: number, activeStep: boolean) => {
      const step = steps[index];
      const completedStep = step.completed;
      const confirmationStep = numberOfSteps - 1 === index && activeStep;
      const label = step.stepName;

      return (
        <StepLabel
          icon={
            activeStep && completedStep ? (
              <CheckCircleIcon className='breadcrumb-active-label' />
            ) : completedStep || confirmationStep ? (
              <CheckCircleOutlineIcon className='color-green' />
            ) : (
              index + 1
            )
          }>
          <span
            className={
              activeStep && !completedStep && !confirmationStep
                ? 'breadcrumb-active-label'
                : 'breadcrumb-root-label'
            }>
            {label}
          </span>
        </StepLabel>
      );
    };

    const getMobileContent = () => {
      const isErrorPage = getActiveStepIndex() > numberOfSteps - 1;

      const indexText = isErrorPage
        ? ''
        : `Step ${getActiveStepIndex() + 1} of 5`;

      const nameText = isErrorPage
        ? 'Error'
        : steps[getActiveStepIndex()].stepName;

      return (
        <div className='breadcrumb-mobile-container'>
          <span
            className='breadcrumb-mobile-step-label breadcrumb-mobile-step-label-size'>
            {indexText}
          </span>
          <br />
          <span className='breadcrumb-mobile-active-label'>{nameText}</span>
        </div>
      );
    };

    const updateBreadcrumb = () => {
      setSteps(
        steps.map((s, index) => ({ ...s, completed: isStepCompleted(index) }))
      );
    };

    useEffect(() => {
      updateBreadcrumb();
    }, [currentPath, navigationStatus]);

    const activeStepIndex = getActiveStepIndex();

    return (
      <section aria-label='Progress'>
        <div className='mobile-hide desktop-navbar'>
          <Stepper activeStep={activeStepIndex} nonLinear={true}>
            {steps.map((step, index) => {
              return (
                <Step key={step.stepName}>
                  {getLabelContent(index, index === activeStepIndex)}
                </Step>
              );
            })}
          </Stepper>
          <br />
        </div>
        <div className='desktop-hide'>
          <div className='mobile-navbar'>{getMobileContent()}</div>
        </div>
      </section>
    );
  },
  (prevProps, nextProps) => {
    if (
      prevProps.products.some((p) => p.isSelected === true) ===
        nextProps.products.some((p) => p.isSelected === true) &&
      prevProps.navigationStatus === nextProps.navigationStatus &&
      prevProps.currentPath === nextProps.currentPath
    ) {
      return true;
    }
    return false;
  }
);

export default NavStepper;
